import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { Loader2 } from "lucide-react";
import { JenisRuang, Option, Periode, RuangCbt } from "@/types";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getListPeriode } from "@/services/getData";
import { listJenisRuang } from "@/constants";

const postData = async (newData: RuangCbt) => {
  const response = await api.post("/setting/ruang-cbt/store", newData);
  return response.data;
};

const patchData = async (newData: RuangCbt) => {
  const response = await api.patch("/setting/ruang-cbt/update", newData);
  return response.data;
};

const deleteData = async (deletedData: { id_ref_ruang_cbt: string }) => {
  const response = await api({
    url: "/setting/ruang-cbt/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: RuangCbt | null;
  closeModal: () => void;
}) {
  const { data: dataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });
  const filteredListPeriode = dataPeriode?.filter((item: Periode) => {
    // return item.jalur_jenis?.is_cbt_internal;
    return item;
  });

  const [form, setForm] = useState<RuangCbt>({
    id_ref_ruang_cbt: selectedRow?.id_ref_ruang_cbt || "",
    id_sys_periode: String(selectedRow?.id_sys_periode) || "",
    nama_ruang: selectedRow?.nama_ruang || "",
    alamat_ruang: selectedRow?.alamat_ruang || "",
    kode_pos: selectedRow?.kode_pos || "",
    kapasitas_ruang: selectedRow?.kapasitas_ruang || 0,
    urutan_ruang: selectedRow?.urutan_ruang || 0,
    jenis_ruang: selectedRow?.jenis_ruang || null,
  });

  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listRuangCbt"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate(form);
  };

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Ruang CBT"
          description={`Apakah anda yakin akan menghapus data ruang CBT "${selectedRow?.nama_ruang}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Ruang CBT" : "Update Data Ruang CBT"}
      />
      <div>
        <Label>Periode</Label>
        <Select
          required
          value={form.id_sys_periode}
          onValueChange={(value) => {
            if (!value) return;
            setForm((prev) => ({ ...prev, id_sys_periode: value }));
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Periode"} />
          </SelectTrigger>
          <SelectContent>
            {filteredListPeriode?.map((periode: Periode) => (
              <SelectItem
                key={periode.id_sys_periode}
                value={`${periode.id_sys_periode}`}
              >
                {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                Gelombang {periode.gelombang}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Jenis Ruang (Daring/Luring)</Label>
        <Select
          required
          value={form.jenis_ruang || ""}
          onValueChange={(value) => {
            if (!value) return;
            setForm((prev) => ({ ...prev, jenis_ruang: value as JenisRuang }));
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Jenis Ruang"} />
          </SelectTrigger>
          <SelectContent>
            {listJenisRuang?.map((jenisRuang: Option) => (
              <SelectItem key={jenisRuang.value} value={`${jenisRuang.value}`}>
                {jenisRuang?.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Urutan Ruang</Label>
        <Input
          placeholder="Ketik urutan ruang..."
          value={form.urutan_ruang}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              urutan_ruang: Number(event.target.value),
            }))
          }
        />
      </div>
      <div>
        <Label>Nama Ruang</Label>
        <Input
          placeholder="Ketik nama ruang..."
          value={form.nama_ruang}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nama_ruang: event.target.value }))
          }
        />
      </div>
      <div>
        <Label>Kapasitas Ruang</Label>
        <Input
          placeholder="Ketik kapasitas ruang..."
          value={form.kapasitas_ruang}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kapasitas_ruang: Number(event.target.value),
            }))
          }
        />
      </div>
      <div>
        <Label>Alamat Ruang</Label>
        <Input
          placeholder="Ketik alamat ruang..."
          value={form.alamat_ruang}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              alamat_ruang: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Kode POS</Label>
        <Input
          placeholder="Ketik kode pos..."
          value={form.kode_pos}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              kode_pos: event.target.value,
            }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  );
}
